import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { addBreadcrumb } from '../../config/sentry';
import './WysiwygEditor.css';

export function isValidUrl(str) {
  const trimmed = str.trim();
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol);
  } catch {
    if (/^[a-zA-Z0-9][\w.-]+\.[a-z]{2,}/i.test(trimmed)) {
      return isValidUrl('https://' + trimmed);
    }
    return false;
  }
}

export function normalizeUrl(str) {
  const trimmed = str.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return 'https://' + trimmed;
}

const MenuBar = ({ editor }) => {
  const addLink = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    const existingHref = editor.getAttributes('link').href || '';

    const url = window.prompt('Enter URL:', existingHref);
    if (url === null) return;

    const trimmed = url.trim();
    if (!trimmed) {
      if (editor.isActive('link')) {
        editor.chain().focus().unsetLink().run();
      }
      return;
    }

    if (!isValidUrl(trimmed)) {
      addBreadcrumb('WysiwygEditor: invalid URL rejected', {
        category: 'editor.link',
        level: 'warning',
        data: { url: trimmed },
      });
      window.alert('Please enter a valid URL (e.g. https://example.com)');
      return;
    }

    const href = normalizeUrl(trimmed);

    addBreadcrumb('WysiwygEditor: link added', {
      category: 'editor.link',
      level: 'info',
      data: { href, hasSelection },
    });

    if (hasSelection) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    } else {
      const displayText = trimmed.replace(/^https?:\/\//, '');
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${href}">${displayText}</a>`)
        .run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="wysiwyg-toolbar">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
        title="Bold (Ctrl+B)"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
        title="Italic (Ctrl+I)"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
        title="Strikethrough"
      >
        <s>S</s>
      </button>
      
      <span className="toolbar-divider" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
        title="Heading 2"
      >
        H2
      </button>
      
      <span className="toolbar-divider" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        title="Bullet List"
      >
        • List
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        title="Numbered List"
      >
        1. List
      </button>
      
      <span className="toolbar-divider" />
      
      <button
        type="button"
        onClick={addLink}
        className={editor.isActive('link') ? 'is-active' : ''}
        title="Add Link"
      >
        🔗 Link
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive('link')}
        title="Remove Link"
      >
        ✂️
      </button>
      <span className="toolbar-divider" />
      
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
        title="Quote"
      >
        ❝ Quote
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
        title="Code Block"
      >
        {'</>'}
      </button>
    </div>
  );
};

const WysiwygEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing here...', 
  style = {} 
}) => {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'editor-link',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      if (onChange) {
        const html = editor.getHTML();
        const text = editor.getText();
        onChange(text, html);
      }
    },
  });

  useEffect(() => {
    if (!editor || isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const currentHtml = editor.getHTML();
    if (value !== currentHtml && value !== undefined) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  return (
    <div className="wysiwyg-editor-container" style={style}>
      <div className="wysiwyg-header">
        <span className="wysiwyg-label">Rich Text Editor</span>
        <span className="wysiwyg-hint">Type directly - formatting appears instantly!</span>
      </div>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="wysiwyg-content" />
      <div className="wysiwyg-footer">
        💡 Select text and click buttons to format • Paste URLs to auto-link
      </div>
    </div>
  );
};

export default WysiwygEditor;
